import type { ReactNode } from 'react';

import { cn } from '@/lib/utils';

/**
 * EmptyState — icon, one sentence, one primary action (05 §6.3).
 *
 * "Nothing here" is a state the TA has to be able to leave: every empty list in
 * this app offers the action that fills it ("no students — import from Excel").
 */
export interface EmptyStateProps {
  title: string;
  description?: string;
  /** A lucide icon element, e.g. `<Users className="size-6" />`. */
  icon?: ReactNode;
  action?: ReactNode;
  className?: string;
  'data-testid'?: string;
}

export function EmptyState({
  title,
  description,
  icon,
  action,
  className,
  'data-testid': testId = 'empty-state',
}: EmptyStateProps) {
  return (
    <div
      data-testid={testId}
      className={cn(
        'flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed px-6 py-12 text-center',
        className,
      )}
    >
      {icon ? (
        <span
          aria-hidden="true"
          className="grid size-10 place-items-center rounded-full bg-muted text-muted-foreground"
        >
          {icon}
        </span>
      ) : null}
      <div className="space-y-1">
        <p className="text-sm font-medium text-foreground">{title}</p>
        {description ? (
          <p className="mx-auto max-w-sm text-sm text-muted-foreground">{description}</p>
        ) : null}
      </div>
      {action ? <div className="mt-1">{action}</div> : null}
    </div>
  );
}
